import { z } from "zod";
import { actorKindSchema } from "./enums";

/**
 * Actions written to the audit log. See docs/architecture/security.md.
 * Named `entity.pastTense`. Clients translate them in the audit viewer.
 * Add an action here in the task that first records it.
 */
export const AUDIT_ACTIONS = [
	"restaurant.created",
	"restaurant.updated",
	"restaurant.statusChanged",
	"invite.created",
	"invite.revoked",
	"member.joined",
	"member.roleChanged",
	"member.disabled",
	"member.enabled",
	"impersonation.started",
	"platformAdmin.added",
	"platformAdmin.removed",
	"item.priceChanged",
	"table.qrRegenerated",
	"order.itemCancelled",
	"order.cancelled",
	"platformInvoice.created",
	"platformInvoice.voided",
	"platformPayment.recorded",
	"platformPayment.reversed",
	"subscription.trialExtended",
	"subscription.graceExtended",
	"subscription.reactivated",
	"subscription.planChanged",
	"subscription.customPriceChanged",
	"subscription.statusChanged",
	"restaurant.slugRedirected",
	"plan.created",
	"plan.updated",
	"platformSettings.updated",
	"site.published",
	"site.visibilityChanged",
	"bill.finalized",
	"bill.discounted",
	"bill.voided",
	"billingSettings.updated",
	"payment.recorded",
	"payment.reversed",
	"bill.reprinted",
	"modifierGroup.priceChanged",
	"taxClass.updated",
	"taxClass.deleted",
	"device.registered",
	"day.closed",
	"member.switched",
	"menuImport.applied",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** Admin > Audit log (P11-T12): `admin.audit.list`, newest first, a page at a time. */
export const auditListInputSchema = z
	.object({
		/** The last ID of the previous page. */
		cursor: z.uuid().optional(),
		limit: z.number().int().min(1).max(100).default(50),
		restaurantId: z.uuid().optional(),
		/** The person who acted. */
		actorUserId: z.uuid().optional(),
		actorKind: actorKindSchema.optional(),
		action: z.enum(AUDIT_ACTIONS).optional(),
		/** India dates, both included. */
		from: z.iso.date().optional(),
		to: z.iso.date().optional(),
	})
	.refine((i) => !i.from || !i.to || i.from <= i.to, {
		message: "The start must not be after the end",
		path: ["to"],
	});
export type AuditListInput = z.input<typeof auditListInputSchema>;

const restaurantRef = z.object({ id: z.uuid(), name: z.string(), slug: z.string() });

export const auditEntrySchema = z.object({
	id: z.uuid(),
	createdAt: z.iso.datetime(),
	/** One of `AUDIT_ACTIONS`; a string so an older client still shows a newer action. */
	action: z.string(),
	entityType: z.string(),
	entityId: z.string().nullable(),
	data: z.record(z.string(), z.unknown()),
	ip: z.string().nullable(),
	/** Null for platform actions. */
	restaurant: restaurantRef.nullable(),
	actor: z.object({
		kind: actorKindSchema,
		userId: z.uuid().nullable(),
		name: z.string().nullable(),
		email: z.string().nullable(),
	}),
});
export type AuditLogEntry = z.infer<typeof auditEntrySchema>;

export const auditListResultSchema = z.object({
	items: z.array(auditEntrySchema),
	nextCursor: z.uuid().nullable(),
	/** The filtered restaurant and person, for the filter labels. */
	restaurant: restaurantRef.nullable(),
	actor: z
		.object({ id: z.uuid(), name: z.string().nullable(), email: z.string().nullable() })
		.nullable(),
});
export type AuditListResult = z.infer<typeof auditListResultSchema>;

/**
 * More > Activity log (P11-T11): the restaurant's own audit log for owners
 * and managers (`audit.view`), newest first, filtered by person, action and
 * dates (India dates, both included).
 */
export const staffAuditListInputSchema = z
	.object({
		cursor: z.uuid().optional(),
		limit: z.number().int().min(1).max(100).default(50),
		actorUserId: z.uuid().optional(),
		action: z.enum(AUDIT_ACTIONS).optional(),
		from: z.iso.date().optional(),
		to: z.iso.date().optional(),
	})
	.refine((i) => !i.from || !i.to || i.from <= i.to, {
		message: "The start must not be after the end",
		path: ["to"],
	});
export type StaffAuditListInput = z.input<typeof staffAuditListInputSchema>;

/** An entry as the restaurant sees it: no IP address, no restaurant (it is theirs). */
export const staffAuditEntrySchema = auditEntrySchema.omit({ ip: true, restaurant: true });
export type StaffAuditEntry = z.infer<typeof staffAuditEntrySchema>;

export const staffAuditListResultSchema = z.object({
	items: z.array(staffAuditEntrySchema),
	nextCursor: z.uuid().nullable(),
	/** Everyone who is or was a member, for the person filter. */
	people: z.array(
		z.object({ id: z.uuid(), name: z.string().nullable(), email: z.string().nullable() }),
	),
});
export type StaffAuditListResult = z.infer<typeof staffAuditListResultSchema>;
